import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { Dispatch } from "react";
import CountryFlag from "@entrilia/shared/components/CountryFlag";
import useSettingsContext from "../../../useSettingsContext";
import type { Action, NewBankAccountState } from "./newBankAccountState";
import { getBankAccountFormLayout } from "./newBankAccountState";

interface Props {
  state: NewBankAccountState;
  dispatch: Dispatch<Action>;
}

const NewBankAccountForm = ({ state, dispatch }: Props) => {
  const { dictionaries } = useSettingsContext();

  const countriesOptions = dictionaries.countries.map(({ code }) => code);
  const currenciesOptions = dictionaries.currencies.map(({ code }) => code);
  const statesOptions = dictionaries.states.map(({ code }) => code);

  const { showAbaNumber, showIban, isSwiftRequired, showBankAddress, showInstitutionAndTransitNumbers } =
    getBankAccountFormLayout(state.bankAccountForm.countryCode);

  return (
    <Stack spacing={4} sx={{ py: 2.5 }}>
      <Stack spacing={2.5}>
        <Typography variant="subtitle2">Bank Details</Typography>

        <Autocomplete
          fullWidth
          includeInputInList
          options={countriesOptions}
          value={state.bankAccountForm.values.country}
          onChange={(_e, newValue) => dispatch({ type: "update_field", field: "country", value: newValue || "" })}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Bank Country"
              error={!!state.bankAccountForm.errors.country}
              helperText={state.bankAccountForm.errors.country}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: state.bankAccountForm.countryCode ? (
                    <CountryFlag
                      countryCodeAlpha2={state.bankAccountForm.countryCode}
                      typographyProps={{ sx: { ml: 1 } }}
                    />
                  ) : undefined,
                },
              }}
            />
          )}
        />

        <TextField
          required
          fullWidth
          label="Bank Name"
          variant="outlined"
          value={state.bankAccountForm.values.bankName}
          onChange={(e) => dispatch({ type: "update_field", field: "bankName", value: e.target.value })}
          error={!!state.bankAccountForm.errors.bankName}
          helperText={state.bankAccountForm.errors.bankName}
        />

        <Stack direction="row" spacing={2}>
          {showAbaNumber && (
            <TextField
              required
              fullWidth
              label="ABA Routing Number"
              variant="outlined"
              value={state.bankAccountForm.values.abaNumber}
              onChange={(e) => dispatch({ type: "update_field", field: "abaNumber", value: e.target.value })}
              error={!!state.bankAccountForm.errors.abaNumber}
              helperText={state.bankAccountForm.errors.abaNumber}
            />
          )}

          <TextField
            required={isSwiftRequired}
            fullWidth
            label="SWIFT / BIC Code"
            variant="outlined"
            value={state.bankAccountForm.values.swiftCode}
            onChange={(e) => dispatch({ type: "update_field", field: "swiftCode", value: e.target.value })}
            error={!!state.bankAccountForm.errors.swiftCode}
            helperText={state.bankAccountForm.errors.swiftCode}
          />
        </Stack>
      </Stack>
      {showBankAddress && (
        <Stack spacing={2.5}>
          <Typography variant="subtitle2">Bank Address</Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              required
              fullWidth
              label="Address Line 1"
              variant="outlined"
              value={state.bankAccountForm.values.bankAddress}
              onChange={(e) => dispatch({ type: "update_field", field: "bankAddress", value: e.target.value })}
              error={!!state.bankAccountForm.errors.bankAddress}
              helperText={state.bankAccountForm.errors.bankAddress}
            />
            <TextField
              fullWidth
              label="Address Line 2"
              variant="outlined"
              value={state.bankAccountForm.values.bankAddress2}
              onChange={(e) => dispatch({ type: "update_field", field: "bankAddress2", value: e.target.value })}
              error={!!state.bankAccountForm.errors.bankAddress2}
              helperText={state.bankAccountForm.errors.bankAddress2}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              required
              fullWidth
              label="City"
              variant="outlined"
              value={state.bankAccountForm.values.bankCity}
              onChange={(e) => dispatch({ type: "update_field", field: "bankCity", value: e.target.value })}
              error={!!state.bankAccountForm.errors.bankCity}
              helperText={state.bankAccountForm.errors.bankCity}
            />
            <Autocomplete
              fullWidth
              freeSolo
              includeInputInList
              options={statesOptions}
              value={state.bankAccountForm.values.bankState}
              onInputChange={(_e, newValue) =>
                dispatch({ type: "update_field", field: "bankState", value: newValue || "" })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="State / Province / Region"
                  error={!!state.bankAccountForm.errors.bankState}
                  helperText={state.bankAccountForm.errors.bankState}
                />
              )}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              required
              fullWidth
              label="ZIP / Postal Code"
              variant="outlined"
              value={state.bankAccountForm.values.bankZipCode}
              onChange={(e) => dispatch({ type: "update_field", field: "bankZipCode", value: e.target.value })}
              error={!!state.bankAccountForm.errors.bankZipCode}
              helperText={state.bankAccountForm.errors.bankZipCode}
            />
            <Box sx={{ width: "100%" }} />
          </Stack>
        </Stack>
      )}
      <Stack spacing={2.5}>
        <Typography variant="subtitle2">Account Details</Typography>
        <TextField
          required
          fullWidth
          label="Beneficiary Account Name"
          variant="outlined"
          value={state.bankAccountForm.values.accountName}
          onChange={(e) => dispatch({ type: "update_field", field: "accountName", value: e.target.value })}
          error={!!state.bankAccountForm.errors.accountName}
          helperText={
            state.bankAccountForm.errors.accountName ||
            "Name of the account holder who receives the funds. Defaults to the investor name; change it if payments go to a different beneficiary."
          }
        />

        <Stack spacing={0.5}>
          <Stack direction="row" spacing={2}>
            {showIban && (
              <TextField
                fullWidth
                label="IBAN"
                variant="outlined"
                value={state.bankAccountForm.values.iban}
                onChange={(e) => dispatch({ type: "update_field", field: "iban", value: e.target.value })}
                error={!!state.bankAccountForm.errors.iban || !!state.bankAccountForm.errors.ibanAndAccountNumber}
                helperText={state.bankAccountForm.errors.iban || state.bankAccountForm.errors.ibanAndAccountNumber}
              />
            )}

            <TextField
              required={!showIban}
              fullWidth
              label="Account Number"
              variant="outlined"
              value={state.bankAccountForm.values.accountNumber}
              onChange={(e) => dispatch({ type: "update_field", field: "accountNumber", value: e.target.value })}
              error={
                !!state.bankAccountForm.errors.accountNumber || !!state.bankAccountForm.errors.ibanAndAccountNumber
              }
              helperText={
                state.bankAccountForm.errors.accountNumber || state.bankAccountForm.errors.ibanAndAccountNumber
              }
            />

            <Autocomplete
              includeInputInList
              options={currenciesOptions}
              value={state.bankAccountForm.values.currencyCode}
              onChange={(_e, newValue) =>
                dispatch({ type: "update_field", field: "currencyCode", value: newValue || "" })
              }
              sx={{ width: "10rem" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Currency"
                  error={!!state.bankAccountForm.errors.currencyCode}
                  helperText={state.bankAccountForm.errors.currencyCode}
                />
              )}
            />
          </Stack>
          {showIban && <FormHelperText>Please enter either IBAN or account number, without spaces.</FormHelperText>}
        </Stack>

        {showInstitutionAndTransitNumbers && (
          <Stack direction="row" spacing={2}>
            <TextField
              required
              fullWidth
              label="Institution Number"
              variant="outlined"
              value={state.bankAccountForm.values.institutionNumber}
              onChange={(e) => dispatch({ type: "update_field", field: "institutionNumber", value: e.target.value })}
              error={!!state.bankAccountForm.errors.institutionNumber}
              helperText={state.bankAccountForm.errors.institutionNumber || "3 digits identifying the bank"}
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
            />
            <TextField
              required
              fullWidth
              label="Transit Number"
              variant="outlined"
              value={state.bankAccountForm.values.transitNumber}
              onChange={(e) => dispatch({ type: "update_field", field: "transitNumber", value: e.target.value })}
              error={!!state.bankAccountForm.errors.transitNumber}
              helperText={state.bankAccountForm.errors.transitNumber || "5 digits identifying the branch"}
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
            />
          </Stack>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={state.bankAccountForm.addForFurtherCredit}
              onChange={(_, value) => dispatch({ type: "update_addForFurtherCredit", value })}
              tabIndex={-1}
              sx={{ pl: 0 }}
            />
          }
          label={<Typography>Add for further credit instructions?</Typography>}
        />

        {state.bankAccountForm.addForFurtherCredit && (
          <Stack direction="row" spacing={2}>
            <TextField
              required
              fullWidth
              label="FFC Name"
              variant="outlined"
              value={state.bankAccountForm.values.ffcName}
              onChange={(e) => dispatch({ type: "update_field", field: "ffcName", value: e.target.value })}
              error={!!state.bankAccountForm.errors.ffcName}
              helperText={state.bankAccountForm.errors.ffcName}
            />
            <TextField
              required
              fullWidth
              label="FFC Number"
              variant="outlined"
              value={state.bankAccountForm.values.ffcAccountNumber}
              onChange={(e) => dispatch({ type: "update_field", field: "ffcAccountNumber", value: e.target.value })}
              error={!!state.bankAccountForm.errors.ffcAccountNumber}
              helperText={state.bankAccountForm.errors.ffcAccountNumber}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default NewBankAccountForm;
